import {
  extractPath,
  isOfferingsPostRequest,
} from './injectFlowTypeIntoOfferingsRequest';
import type { InterpolatedRequest } from './fetchDataSource';

const FLAT_OFFERINGS_SEARCH_PATH = '/v1/offerings/search';

/**
 * True only when the resolved request is a POST to the flat
 * `/v1/offerings/search` path. Deliberately narrower than
 * {@link isOfferingsPostRequest}: it must NOT match the bucketed
 * `/v1/offerings/search/match` endpoint, which is out of scope for the
 * page-level presentation-url restriction check. Still requires the broad
 * allowlist check so a non-Offerings endpoint that happens to share the
 * exact path cannot get the runtime value injected.
 */
export function isFlatOfferingsSearchPostRequest(
  req: InterpolatedRequest,
): boolean {
  return (
    isOfferingsPostRequest(req) &&
    extractPath(req.endpoint) === FLAT_OFFERINGS_SEARCH_PATH
  );
}

/**
 * Auto-inject the current `presentationUrl` (the page URL) into flat
 * `/v1/offerings/search` POST bodies, mirroring
 * {@link injectTrafficSourceIntoOfferingsRequest}. This lets offerings-service
 * apply page-level restrictions (`organization.ineligibleUrls`) so restricted
 * offerings are excluded from DSL-driven search results. The runtime value
 * wins over any DSL-authored body field so operators cannot bypass the
 * restriction check via body edits.
 */
export function injectPresentationUrlIntoOfferingsRequest(
  req: InterpolatedRequest,
  presentationUrl: string | undefined,
): InterpolatedRequest {
  if (!presentationUrl || !isFlatOfferingsSearchPostRequest(req)) {
    return req;
  }

  const body = req.body;
  if (body === undefined || body === null) {
    return { ...req, body: { presentationUrl } };
  }
  if (typeof body !== 'object' || Array.isArray(body)) {
    return req;
  }

  return {
    ...req,
    body: { ...(body as Record<string, unknown>), presentationUrl },
  };
}
